import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import EmployeeForm from "../AddEmployee/EmployeeForm";
import usePageName from "../../../../hocs/usePageName";
import { SnackAlertContext } from "../../../../context/SnackAlertContext";
import DeactivateEmployeeModal from "../../../admin/Employees/DeactivateEmployeeDialog";
import RestoreEmployeeModal from "../../../admin/Employees/RestoreEmployeeDialog";
import { LoadingButton } from "@mui/lab";
import { Box } from "@mui/material";

const EditEmployee = () => {
  usePageName({ heading: "User Details" });
  const navigate = useNavigate();
  const { openSnackMessage } = useContext(SnackAlertContext);
  const { id } = useParams();
  const { authToken, ezorder, decoded, role } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);

  // get ID
  const getEmployee = async () => {
    try {
      setLoading(true);
      let data = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };
      const response = await ezorder.get(
        `/company/${decoded.companyId}/users/${id}`,
        data
      );
      setUserDetails(response.data.employee);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeUpdate = async (values) => {
    try {
      setLoading(true);
      let userUpdate = {
        ...userDetails,
        ...values,
      };

      const response = await ezorder.put(
        `/company/${decoded.companyId}/users/${userDetails.id}`,
        userUpdate,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (response.status == 200 || response.status == 201) {
        openSnackMessage("success", "User Updated");
        navigate("/users");
      } else {
        openSnackMessage("error", response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const deactivateEmployeeAccount = async () => {
    try {
      setLoading(true);

      const response = await ezorder.delete(
        `/company/${decoded.companyId}/users/${userDetails.id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (response.status == 200 || response.status == 201) {
        openSnackMessage("success", "User Account Deactivated");
        navigate("/users");
      } else {
        openSnackMessage("error", response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const restoreEmployeeAccount = async () => {
    try {
      setLoading(true);

      const response = await ezorder.put(
        `/company/${decoded.companyId}/users/${userDetails.id}/restore`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      openSnackMessage("success", "User Account Restored");
      navigate("/users");
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEmployee();
  }, [id]);

  if (userDetails) {
    return (
      <>
        <EmployeeForm
          protectEmail={true}
          userDetails={userDetails}
          loading={loading}
          handleSubmit={(values) => {
            handleEmployeeUpdate(values);
          }}
          onBack={() => {
            navigate(`/users`);
          }}
        />
        {userDetails.role != "OWNER" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {userDetails.deletedAt ? (
              <LoadingButton
                color="success"
                onClick={() => {
                  setRestoreDialogOpen(true);
                }}
              >
                Restore Account
              </LoadingButton>
            ) : (
              <LoadingButton
                color="error"
                onClick={() => {
                  setDeactivateDialogOpen(true);
                }}
              >
                Deactivate Account
              </LoadingButton>
            )}
          </Box>
        )}

        <DeactivateEmployeeModal
          open={deactivateDialogOpen}
          handleClose={() => {
            setDeactivateDialogOpen(!deactivateDialogOpen);
          }}
          loading={loading}
          onConfirm={() => {
            deactivateEmployeeAccount();
          }}
        />
        <RestoreEmployeeModal
          open={restoreDialogOpen}
          handleClose={() => {
            setRestoreDialogOpen(!restoreDialogOpen);
          }}
          loading={loading}
          onConfirm={() => {
            restoreEmployeeAccount();
          }}
        />
      </>
    );
  }
};

export default EditEmployee;
